/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import * as default_data from '../config.js'
import CountrySelect from '../Chat/Components/CountrySelect.js';
import OrganizationSelect from '../Chat/Components/OrganizationSelect.js';
import Terms from '../Popups/Terms.js'
import * as log from 'loglevel'
import { Alert, Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MdPhone, MdMail, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { getBannerColor } from '../Utils/classification_helper.js';
import { getLoginLogo } from '../Utils/file_functions.js';

function RegisterForm() {

  // State variables for form inputs, error/success messages, and loading state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [country, setCountry] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  // State variables for form validation
  const [firstNameValid, setFirstNameValid] = useState(true);
  const [lastNameValid, setLastNameValid] = useState(true);
  const [companyValid, setCompanyValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);
  const [phoneValid, setPhoneValid] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [confirmPasswordValid, setConfirmPasswordValid] = useState(true);
  const [countryValid, setCountryValid] = useState(true);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  // Function to handle form submission
  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);

    // Validate each field
    setFirstNameValid(firstName !== '');
    setLastNameValid(lastName !== '');
    setCompanyValid(company !== '');
    setEmailValid(email !== '');
    setPhoneValid(phone !== '');
    setPasswordValid(password !== '');
    setCountryValid(country !== '');
    setConfirmPasswordValid(password === confirmPassword); // recheck password match

    // If any field is invalid, stop the form submission
    if (!firstNameValid || !lastNameValid || !companyValid || !emailValid || !phoneValid || !passwordValid || !countryValid || !confirmPasswordValid) {
      setIsLoading(false);
      return;
    }

    // Send POST request to user service API to register user
    fetch(default_data.default_user_service_url_no_cac + '/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        company: company,
        email: email,
        phone: phone,
        password: password,
        country: country
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(response.statusText);
      })
      .then(data => {
        if (data.status != 200) {
          setIsLoading(false);
          setErrorMessage(data.response);
          return;
        }
        setIsLoading(false);
        // Clear the form and display a success message
        setFirstName('');
        setLastName('');
        setCompany('');
        setEmail('');
        setPhone('');
        setPassword('');
        setConfirmPassword('');
        setCountry('');
        setAcceptedTerms(false);
        setErrorMessage('');

        setSuccessMessage("Your account has been registered!");

        // Wait for 2 seconds before redirecting to email verification page
        const sleep = async (milliseconds) => {
          await new Promise(resolve => {
            return setTimeout(resolve, milliseconds)
          });
        };
        const run_sleep = async () => {
          await sleep(2000);

          if (data.verified && data.verified == true) {
            window.location.href = '/login';
          }
          else {
            window.location.href = '/verify-email';
          }
        }
        run_sleep();
      })
      .catch(error => {
        log.error(`/registerform: ${error.message}`);
        setIsLoading(false);
        setErrorMessage('Internal server error. Please contact customer support.');
      });
  };

  const setAndCheckPassword = (password) => {
    setPassword(password);
    // Check if confirm password is set and validate
    if (confirmPassword) {
      setConfirmPasswordValid(password === confirmPassword);
    }
  };

  const checkIfPasswordsMatch = (confirmPassword) => {
    setConfirmPassword(confirmPassword);
    // Validate confirm password against the password
    setConfirmPasswordValid(password === confirmPassword);
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  }

  return (
    <div>
      {
        <div className={`header-message ${getBannerColor(false)}`}>
          {default_data.default_banner_override != null && default_data.default_banner_override != '' ?
            default_data.default_banner_override : 'UNCLASSIFIED'
          }
        </div>
      }
      <div style={{ backgroundColor: '#121212', height: 'calc(100vh - 16px)' }}>
        <Stack sx={{ maxHeight: 'calc(100vh - 16px)' }} direction={"row"} spacing={0}>
          <Box sx={{ width: isTablet ? "100vw" : "50vw", overflowY: 'auto' }} >
            <Container>
              <Box style={{ display: 'flex', justifyContent: isTablet ? 'center' : 'start', marginBottom: '8px', marginTop: '32px' }}>
                <a href="/">
                  <img src="/logo.png" style={{ width: isTablet ? '90px' : '120px' }} alt="Ask Sage" className="logo" />
                </a>
              </Box>
              {/* Form Body */}
              <Row className="justify-content-center ">
                <Col xs="12" md="8" lg="8">
                  <Typography
                    variant='h5' 
                    sx={{ 
                      color: 'white', 
                      fontWeight: '500', 
                      textAlign:{xs:'center', tablet:'start'}, 
                      fontSize: {xs: 'calc(1.325rem + .9vw)', tablet: '2rem' },
                    }}
                  >
                      Create An Account
                  </Typography>
                  <Typography
                    variant='p' 
                    sx={{ 
                      color: 'lightgray', 
                      textAlign:{xs:'center', tablet:'start'}, 
                      fontSize: '14px',
                    }}
                  >
                    Please note that Ask Sage is only accessible to the nationalities listed below. If your nationality is not listed, you are not authorized to use this service without contacting us first. Additionally, if you do not provide your complete real name and organization information, your account will be banned.
                    {default_data.default_hide_cac === true ? '' :
                      " If you have a CAC/PIV smartcard, please to insert it to enable CAC/PIV login as well."
                    }
                  </Typography>             
                  {/* Registration form */}
                  <Form onSubmit={handleSubmit} className='login-form' style={{marginTop: '24px'}}>
                    <Stack direction={{ xs: 'column', tablet: 'row'}} width={1} spacing={isTablet ? 0 : 2}>
                      <FormGroup style={{width: '100%'}}>
                        <Label for="firstName">First Name*</Label>
                        <Input type="text" value={firstName} onChange={event => setFirstName(event.target.value)}
                          style={firstNameValid ? {} : { borderColor: 'red' }} />
                        {!firstNameValid && <div style={{ color: 'red' }}>This field is required.</div>}
                      </FormGroup>
                      <FormGroup style={{width: '100%'}}>
                        <Label for="lastName">Last Name*</Label>
                        <Input type="text" value={lastName} onChange={event => setLastName(event.target.value)}
                          style={lastNameValid ? {} : { borderColor: 'red' }} />
                        {!lastNameValid && <div style={{ color: 'red' }}>This field is required.</div>}
                      </FormGroup>
                    </Stack>
                    <FormGroup>
                      <Label for="company">Organization* (Agency for government employees)</Label>
                      { default_data.default_force_register_organizations !== null ? 
                        <OrganizationSelect value={company} callback={setCompany} organizationValid={companyValid} />
                        :
                          <Input type="text" value={company} onChange={event => setCompany(event.target.value)} style={companyValid ? {} : { borderColor: 'red' }} />
                      }
                      {!companyValid && <div style={{ color: 'red' }}>This field is required.</div>}
                      </FormGroup>
                    <FormGroup>
                      <Label for="email">Email*</Label>
                      <div className='input-wrapper'>
                        <Input type="email" value={email} onChange={event => setEmail(event.target.value)}
                          style={emailValid ? {} : { borderColor: 'red' }} />
                        <MdMail className="input-icon" />
                      </div>
                      {!emailValid && <div style={{ color: 'red' }}>This field is required.</div>}
                    </FormGroup>
                    <FormGroup>
                      <Label for="phone">Phone*</Label>
                      <div className='input-wrapper'>
                        <Input type="tel" value={phone} onChange={event => setPhone(event.target.value)}
                          style={phoneValid ? {} : { borderColor: 'red' }} />
                        <MdPhone className="input-icon" />  
                      </div>
                      {!phoneValid && <div style={{ color: 'red' }}>This field is required.</div>}
                    </FormGroup>
                    <FormGroup>
                      <Label for="password">Password*</Label>
                      <div className='input-wrapper'>
                        <Input autoComplete="off" type={isPasswordVisible ? 'text' : 'password'} value={password} onChange={event => setAndCheckPassword(event.target.value)}
                          style={passwordValid ? {} : { borderColor: 'red' }} />
                        {isPasswordVisible ? (
                          <MdVisibilityOff className="input-icon" style={{cursor: 'pointer'}} onClick={togglePasswordVisibility} />
                        ) : (
                          <MdVisibility className="input-icon" style={{cursor: 'pointer'}} onClick={togglePasswordVisibility} />
                        )}
                      </div>
                      {!passwordValid && <div style={{ color: 'red' }}>This field is required.</div>}
                    </FormGroup>
                    <FormGroup>
                      <Label for="password">Confirm Password*</Label>
                      <div className='input-wrapper'>
                        <Input autoComplete="off" type={isPasswordVisible ? 'text' : 'password'} value={confirmPassword} onChange={event => checkIfPasswordsMatch(event.target.value)}
                          style={confirmPasswordValid ? {} : { borderColor: 'red' }} />
                        {isPasswordVisible ? (
                          <MdVisibilityOff className="input-icon" style={{cursor: 'pointer'}} onClick={togglePasswordVisibility} />
                        ) : (
                          <MdVisibility className="input-icon" style={{cursor: 'pointer'}} onClick={togglePasswordVisibility} />
                        )}
                      </div>
                      {!confirmPasswordValid && <div style={{ color: 'red' }}>Passwords must match.</div>}
                    </FormGroup>
                      {/* Country select component */}
                      <CountrySelect value={country} callback={setCountry} countryValid={countryValid} />
                    <FormGroup>
                      {/* Terms and conditions popup */}
                      <Terms
                        acceptedTerms={acceptedTerms}
                        setAcceptedTerms={setAcceptedTerms}
                      />
                    </FormGroup>
                    {/* Error and success messages */}
                    {errorMessage && <Alert severity='error' sx={{mb: 2}}>{errorMessage}</Alert>}
                    {successMessage && <Alert severity='success' sx={{mb: 2}}>{successMessage}</Alert>}
                    {/* Submit button */}
                    <Button className={'login-form-button'} type="submit" disabled={!acceptedTerms || isLoading}>
                      {isLoading ? 'Loading...' : 'Submit'}
                    </Button>
                  </Form>
                  {/* Login button */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                    <Typography style={{ color: 'white', alignItems:'center', display:'flex' }}>Already have an account?</Typography>
                    <Button color='link' style={{color: 'white' }} onClick={() => window.location.href = '/login'}>
                      Sign In.
                    </Button>
                  </div>
                  {/* Footer */}
                </Col>
              </Row>
              <Row className="justify-content-center reg-page-mode-footer">
                <Col xs="12" md="12" lg="12">
                  Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
                </Col>
              </Row>
            </Container>
          </Box>
          {!isTablet && (
            <div
              className="space-image"
              style={{
                backgroundImage: "url('/space-background.png')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                width: '50vw',
              }}
            >
              <img src={getLoginLogo()} alt="Space Girl" />
            </div>
          )}

        </Stack>
      </div>
    </div>
  );
}

export default RegisterForm;
